import { ofType, Epic, combineEpics } from "redux-observable";
import { of } from "rxjs";
import { catchError, map, mergeMap, takeUntil } from "rxjs/operators";
import { selectAuthTokens } from "../../../features/auth/selectors/auth.selector";
import { categoriesActions } from "../../../features/categories/redux";
import { countriesActions } from "../../../features/countries/redux";
import { dataFieldsActions } from "../../../features/data-fields/redux";
import { downloadDataBrowserAction } from "../../../features/download/redux/download.actions";
import { IAction } from "../../../interface/action.interface";
import { IErrorDto } from "../../../interface/dto/error.dto";
import { IRootState } from "../../../redux/root.reducer";
import {
    selectExportFiltersAndPagination,
    selectFiltersAndPagination,
} from "../selectors/filters-and-pagination";
import { getList } from "../service/lists.api";
import {
    DataBrowserListActionTypes,
    dataBrowserListLoadListsAction,
    dataBrowserListLoadListsSuccessAction,
    dataBrowserListLoadListsErrorAction,
    IDataBrowserListActions,
} from "./lists.actions";

const initDataBrowserListEpic: Epic<IAction, IAction, IRootState> = (action$) =>
    action$.pipe(
        ofType(DataBrowserListActionTypes.DATABROWSER_LIST_INIT),
        mergeMap(() => [
            countriesActions.init(),
            categoriesActions.init(),
            dataFieldsActions.init(),
            dataBrowserListLoadListsAction(),
        ]),
    );

const dataBrowserListLoadEpic: Epic<IAction, IAction, IRootState> = (
    action$,
    state$,
) =>
    action$.pipe(
        ofType(DataBrowserListActionTypes.DATABROWSER_LIST_LOAD_LISTS),
        mergeMap(() => {
            const requestData = selectFiltersAndPagination(state$.value);
            return getList(requestData, selectAuthTokens()).pipe(
                takeUntil(
                    action$.pipe(
                        ofType(
                            DataBrowserListActionTypes.DATABROWSER_LIST_LOAD_LISTS,
                        ),
                    ),
                ),
                map((response) =>
                    dataBrowserListLoadListsSuccessAction(response),
                ),
                catchError((error: IErrorDto) =>
                    of(dataBrowserListLoadListsErrorAction(error.message)),
                ),
            );
        }),
    );

const dataBrowserListLoadOnFilterModifyEpic: Epic<
    IAction,
    IAction,
    IRootState
> = (action$) =>
    action$.pipe(
        ofType(
            DataBrowserListActionTypes.DATABROWSER_LIST_SET_FILTERS,
            DataBrowserListActionTypes.DATABROWSER_LIST_RESET_FILTERS,
            DataBrowserListActionTypes.DATABROWSER_LIST_SET_SORTING,
            DataBrowserListActionTypes.DATABROWSER_LIST_SET_PAGINATION,
        ),
        map(() => dataBrowserListLoadListsAction()),
    );

const dataBrowserExportCatalogEpic: Epic<IAction, IAction, IRootState> = (
    action$,
    state$,
) =>
    action$.pipe(
        ofType<
            IDataBrowserListActions,
            DataBrowserListActionTypes.DATABROWSER_EXPORT_CATALOG
        >(DataBrowserListActionTypes.DATABROWSER_EXPORT_CATALOG),
        mergeMap(() => [
            downloadDataBrowserAction({
                ...selectExportFiltersAndPagination(state$.value),
                listIds: state$.value.dataBrowserList.selectedListIds,
                mode: state$.value.dataBrowserList.selectAll
                    ? "exclude"
                    : "include",
            }),
        ]),
    );

export const dataBrowserListEpic = combineEpics<IAction, IAction, IRootState>(
    dataBrowserListLoadEpic,
    dataBrowserListLoadOnFilterModifyEpic,
    initDataBrowserListEpic,
    dataBrowserExportCatalogEpic,
);
